import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BiEnvelope, BiMessageDetail, BiPhone } from "react-icons/bi";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTelegram,
  FaTwitter,
  FaWhatsapp,
} from "react-icons/fa";
import { SiYoutube } from "react-icons/si";
import {
  sanitizePhoneInput,
  normalizeTelegramHandle,
  normalizeTwitterHandle,
  normalizeInstagramHandle,
  normalizeLinkedInPath,
  normalizeFacebookPath,
  normalizeYouTubePath,
} from "./linkNormalizers";
import { EMAIL_PATTERN_SIMPLE } from "./validationPatterns";

export interface CommunicationTemplate {
  id: string;
  label: string;
  icon: ReactNode;
  iconId: string;
  placeholder: string;
  buildLink: (value: string) => string;
  fallbackLink?: (value: string) => string;
  inputLabel: string;
  description: string;
  validator?: (value: string) => boolean;
}

export const COMMUNICATION_TEMPLATES: CommunicationTemplate[] = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: <FaWhatsapp className="text-green-500" />,
    iconId: "whatsapp",
    placeholder: "+1234567890",
    buildLink: (raw: string) => {
      const sanitized = sanitizePhoneInput(raw);
      const numeric = sanitized.replace(/^\+/, "");
      return `whatsapp://send?phone=${encodeURIComponent(numeric)}`;
    },
    fallbackLink: (raw: string) => {
      const sanitized = sanitizePhoneInput(raw);
      const numeric = sanitized.replace(/^\+/, "");
      return `https://wa.me/${encodeURIComponent(numeric)}`;
    },
    inputLabel: "Phone Number",
    description: "Link to WhatsApp chat",
    validator: (value: string) => {
      const sanitized = sanitizePhoneInput(value);
      const numeric = sanitized.replace(/^\+/, "");
      return numeric.length > 0 && /^\d+$/.test(numeric);
    },
  },
  {
    id: "telegram",
    label: "Telegram",
    icon: <FaTelegram className="text-blue-500" />,
    iconId: "telegram",
    placeholder: "username",
    buildLink: (raw: string) => {
      const username = normalizeTelegramHandle(raw);
      return `https://t.me/${encodeURIComponent(username)}`;
    },
    fallbackLink: (raw: string) => {
      const username = normalizeTelegramHandle(raw);
      return `https://t.me/${encodeURIComponent(username)}`;
    },
    inputLabel: "Username",
    description: "Link to Telegram chat",
    validator: (value: string) => {
      const username = normalizeTelegramHandle(value);
      return /^[a-zA-Z0-9_]{5,32}$/.test(username);
    },
  },
  {
    id: "phone",
    label: "Phone Call",
    icon: <BiPhone className="text-blue-600" />,
    iconId: "phone",
    placeholder: "+1234567890",
    buildLink: (raw: string) => `tel:${sanitizePhoneInput(raw)}`,
    fallbackLink: (raw: string) => `tel:${sanitizePhoneInput(raw)}`,
    inputLabel: "Phone Number",
    description: "Make a phone call",
    validator: (value: string) => {
      const sanitized = sanitizePhoneInput(value);
      const numeric = sanitized.replace(/^\+/, "");
      return numeric.length > 0 && /^\d+$/.test(numeric);
    },
  },
  {
    id: "sms",
    label: "SMS",
    icon: <BiMessageDetail className="text-green-500" />,
    iconId: "sms",
    placeholder: "+1234567890",
    buildLink: (raw: string) => {
      const sanitized = sanitizePhoneInput(raw);
      return `sms:${sanitized}`;
    },
    fallbackLink: (raw: string) => {
      const sanitized = sanitizePhoneInput(raw);
      return `sms:${sanitized}`;
    },
    inputLabel: "Phone Number",
    description: "Send a text message",
    validator: (value: string) => {
      const sanitized = sanitizePhoneInput(value);
      const numeric = sanitized.replace(/^\+/, "");
      return numeric.length > 0 && /^\d+$/.test(numeric);
    },
  },
  {
    id: "email",
    label: "Email",
    icon: <BiEnvelope className="text-red-500" />,
    iconId: "email",
    placeholder: "contact@example.com",
    buildLink: (raw: string) => `mailto:${raw.trim()}`,
    fallbackLink: (raw: string) => `mailto:${raw.trim()}`,
    inputLabel: "Email Address",
    description: "Send an email",
    validator: (value: string) => EMAIL_PATTERN_SIMPLE.test(value),
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    icon: <FaLinkedin className="text-blue-700" />,
    iconId: "linkedin",
    placeholder: "company-name or in/username",
    buildLink: (raw: string) => {
      const path = normalizeLinkedInPath(raw);
      if (path.startsWith("company/")) {
        const slug = path.replace(/^company\//, "");
        return `linkedin://company/${encodeURIComponent(slug)}`;
      }
      const slug = path.replace(/^in\//, "");
      return `linkedin://profile/${encodeURIComponent(slug)}`;
    },
    fallbackLink: (raw: string) => {
      const path = normalizeLinkedInPath(raw);
      return `https://linkedin.com/${path}`;
    },
    inputLabel: "LinkedIn Profile/Company",
    description: "Link to LinkedIn profile or company page",
    validator: (value: string) => {
      const path = normalizeLinkedInPath(value);
      return /^(in\/|company\/)?[a-zA-Z0-9-]+$/.test(path);
    },
  },
  {
    id: "twitter",
    label: "Twitter/X",
    icon: <FaTwitter className="text-black" />,
    iconId: "twitter",
    placeholder: "username",
    buildLink: (raw: string) => {
      const handle = normalizeTwitterHandle(raw);
      return `twitter://user?screen_name=${encodeURIComponent(handle)}`;
    },
    fallbackLink: (raw: string) => {
      const handle = normalizeTwitterHandle(raw);
      return `https://twitter.com/${handle}`;
    },
    inputLabel: "Twitter Username",
    description: "Link to Twitter profile",
    validator: (value: string) => {
      const handle = normalizeTwitterHandle(value);
      return /^[a-zA-Z0-9_]{1,15}$/.test(handle);
    },
  },
  {
    id: "instagram",
    label: "Instagram",
    // design-ok: charte de marque, rose Instagram
    icon: <FaInstagram className="text-pink-500" />,
    iconId: "instagram",
    placeholder: "username",
    buildLink: (raw: string) => {
      const handle = normalizeInstagramHandle(raw);
      return `instagram://user?username=${encodeURIComponent(handle)}`;
    },
    fallbackLink: (raw: string) => {
      const handle = normalizeInstagramHandle(raw);
      return `https://instagram.com/${handle}`;
    },
    inputLabel: "Instagram Username",
    description: "Link to Instagram profile",
    validator: (value: string) => {
      const handle = normalizeInstagramHandle(value);
      return /^[a-zA-Z0-9._]{1,30}$/.test(handle);
    },
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: <FaFacebook className="text-blue-600" />,
    iconId: "facebook",
    placeholder: "page-name or profile.php?id=123",
    buildLink: (raw: string) => {
      const path = normalizeFacebookPath(raw);
      const targetUrl = `https://facebook.com/${path}`;
      return `fb://facewebmodal/f?href=${encodeURIComponent(targetUrl)}`;
    },
    fallbackLink: (raw: string) => {
      const path = normalizeFacebookPath(raw);
      return `https://facebook.com/${path}`;
    },
    inputLabel: "Facebook Page/Profile",
    description: "Link to Facebook page or profile",
    validator: (value: string) => {
      const path = normalizeFacebookPath(value);
      return /^[a-zA-Z0-9./?=&-]+$/.test(path);
    },
  },
  {
    id: "youtube",
    label: "YouTube",
    icon: <SiYoutube className="text-red-600" />,
    iconId: "youtube",
    placeholder: "watch?v=ID or channel",
    buildLink: (raw: string) => {
      const path = normalizeYouTubePath(raw);
      if (!path) return "youtube://";
      if (path.startsWith("watch?v=")) {
        const videoId = path.substring("watch?v=".length);
        return `youtube://watch?v=${encodeURIComponent(videoId)}`;
      }
      return `youtube://www.youtube.com/${path}`;
    },
    fallbackLink: (raw: string) => {
      const path = normalizeYouTubePath(raw);
      return `https://youtube.com/${path}`;
    },
    inputLabel: "YouTube Path",
    description: "Link to YouTube video or channel",
    validator: (value: string) => Boolean(normalizeYouTubePath(value)),
  },
];

export const DEEP_LINK_PROTOCOLS = [
  "mailto",
  "tel",
  "sms",
  "whatsapp",
  "telegram",
  "tg",

  "twitter",
  "instagram",
  "facebook",
  "fb",
  "linkedin",

  "youtube",
];

const TEMPLATE_BY_ID = new Map<string, CommunicationTemplate>(
  COMMUNICATION_TEMPLATES.map((template) => [
    template.id.toLowerCase(),
    template,
  ])
);

const PROTOCOL_TO_TEMPLATE_ID: Record<string, string> = {
  whatsapp: "whatsapp",
  telegram: "telegram",
  tg: "telegram",
  tel: "phone",
  sms: "sms",
  mailto: "email",

  twitter: "twitter",
  instagram: "instagram",
  facebook: "facebook",
  fb: "facebook",
  linkedin: "linkedin",
  youtube: "youtube",
};

export const findTemplateByProtocol = (
  protocol: string,
  templates: CommunicationTemplate[] = COMMUNICATION_TEMPLATES
): CommunicationTemplate | undefined => {
  if (!protocol) {
    return undefined;
  }

  const normalizedProtocol = protocol.toLowerCase();
  const templateId = (
    PROTOCOL_TO_TEMPLATE_ID[normalizedProtocol] || normalizedProtocol
  ).toLowerCase();

  if (templates === COMMUNICATION_TEMPLATES) {
    return TEMPLATE_BY_ID.get(templateId);
  }

  return templates.find((template) => template.id.toLowerCase() === templateId);
};

export const COMMUNICATION_ICON_IDS = COMMUNICATION_TEMPLATES.map(
  (template) => template.iconId
);

export const useCommunicationTemplates = (): CommunicationTemplate[] => {
  const { t } = useTranslation();

  return useMemo(() => {
    return COMMUNICATION_TEMPLATES.map((template) => ({
      ...template,
      label: t(`communicationTemplates.${template.id}.label`, template.label),
      placeholder: t(
        `communicationTemplates.${template.id}.placeholder`,
        template.placeholder
      ),
      inputLabel: t(
        `communicationTemplates.${template.id}.inputLabel`,
        template.inputLabel
      ),
      description: t(
        `communicationTemplates.${template.id}.description`,
        template.description
      ),
    }));
  }, [t]);
};

export const useFindTemplateByProtocol = () => {
  const templates = useCommunicationTemplates();

  return (protocol: string): CommunicationTemplate | undefined => {
    return findTemplateByProtocol(protocol, templates);
  };
};
